import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { sendApplicationFormSchema } from '../../schema';
import { zodResolver } from '@hookform/resolvers/zod';
import { SendApplicationFormTemplates, SendApplicationFormValues } from '../../types';
import { Button, Theme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { RadioGroupField } from 'components/form';
import { sendMethodOptions, TestIds } from '../../constants';
import { FormSection } from './FormSection';
import {
  Send as SendIcon,
  Person as PersonIcon,
  LocalShipping as LocalShippingIcon,
  InsertDriveFile as InsertDriveFileIcon,
} from '@mui/icons-material';
import { ContactDetailsSection } from './ContactDetailsSection';
import { EquipmentSection } from './EquipmentSection';
import { TemplateSection } from './TemplateSection';
import { SelectOption } from 'global';
import { useNotifications } from 'modules/notification';
import { SendOcaLinkSendMethods } from '@trnsact/trnsact-shared-types';
import { SendOcaLinkMutationResponse } from 'modules/sendCreditApplication/api/types';

interface Props {
  lenderProfilesWithOcaOptions: SelectOption<string>[];
  isSendOcaLinkLoading: boolean;
  handleSendOcaLink: (values: SendApplicationFormValues) => Promise<SendOcaLinkMutationResponse>;
}

export const SendApplicationForm = ({
  lenderProfilesWithOcaOptions,
  isSendOcaLinkLoading,
  handleSendOcaLink,
}: Props) => {
  const classes = useStyles();

  const { showNotification } = useNotifications();

  const form = useForm<SendApplicationFormValues>({
    resolver: zodResolver(sendApplicationFormSchema),
    defaultValues: {
      sendMethod: SendOcaLinkSendMethods.TextAndEmail,
      crediApplicationTemplate: SendApplicationFormTemplates.Personal,
      equipment: {
        amountRequested: undefined,
        quantity: undefined,
        mileage: undefined,
        year: '',
        condition: '',
      },
    },
  });

  const handleSubmit = async (formValues: SendApplicationFormValues) => {
    const result = await handleSendOcaLink(formValues);

    if (result?.data?.sendOcaLink?.success) {
      showNotification('Success');
      form.reset();
    } else {
      showNotification('Error', { type: 'error' });
    }
  };

  return (
    <FormProvider {...form}>
      <RadioGroupField
        row
        label="Send your credit app link"
        control={form.control}
        options={sendMethodOptions}
        name="sendMethod"
        data-testid={TestIds.SendViaRadioGroup}
      />
      <FormSection title="Contact Details" icon={<PersonIcon className={classes.formSectionIcon} />}>
        <ContactDetailsSection />
      </FormSection>
      <FormSection
        title="Equipment"
        icon={<LocalShippingIcon className={classes.formSectionIcon} />}
        isExpandable={true}
        isExpanded={false}
      >
        <EquipmentSection />
      </FormSection>
      {lenderProfilesWithOcaOptions.length > 0 && (
        <FormSection
          title="Credit Application Template"
          icon={<InsertDriveFileIcon className={classes.formSectionIcon} />}
        >
          <TemplateSection lenderProfilesWithOcaOptions={lenderProfilesWithOcaOptions} />
        </FormSection>
      )}

      <Button
        variant="contained"
        sx={{ alignSelf: 'flex-start' }}
        onClick={form.handleSubmit(handleSubmit)}
        endIcon={<SendIcon />}
        loading={isSendOcaLinkLoading}
      >
        Send
      </Button>
    </FormProvider>
  );
};

const useStyles = makeStyles<Theme>(({ palette }) => ({
  formSectionIcon: {
    color: palette.grey[500],
    fontSize: 20,
  },
}));
